import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Gallery, CloudinaryImage } from '../../types/Gallery';
import UploadWidget from '../Cloudinary/UploadWidget';
import CloudinaryImageBrowser from '../Cloudinary/CloudinaryImageBrowser';
import HeroImageEditor, { HeroImageSettings } from './HeroImageEditor';
import { categoryIsChosen, getCategoryOptions, loadCategoriesFromServer, selectedCategorySlugs, toggleCategorySlug, uniqueCategorySlugs } from '../../utils/categories';
import './GalleryEditModal.css';

interface GalleryEditModalProps {
  gallery: Gallery | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (gallery: Gallery) => void;
  onDelete?: (galleryId: string) => void;
}


const GalleryEditModal: React.FC<GalleryEditModalProps> = ({ 
  gallery, 
  isOpen, 
  onClose, 
  onSave, 
  onDelete 
}) => {
  const [galleryName, setGalleryName] = useState('');
  const [galleryCategories, setGalleryCategories] = useState<string[]>([]);
  const [galleryDescription, setGalleryDescription] = useState('');
  const [galleryExcerpt, setGalleryExcerpt] = useState('');
  const [galleryImages, setGalleryImages] = useState<CloudinaryImage[]>([]);
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());
  const [categoryOptions, setCategoryOptions] = useState<{ value: string; label: string; color: string }[]>([]);
  const [showImageManagement, setShowImageManagement] = useState(false);
  const [showHeroSelection, setShowHeroSelection] = useState(false);
  const [draggedImageIndex, setDraggedImageIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [isGroupDragging, setIsGroupDragging] = useState(false);
  const [heroImagePosition, setHeroImagePosition] = useState<'default' | 'center' | 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'face' | 'custom' | 'crop'>('crop');
  const [heroImageZoom, setHeroImageZoom] = useState<number>(1.0);
  const [heroImageCustomX, setHeroImageCustomX] = useState<number>(50);
  const [heroImageCustomY, setHeroImageCustomY] = useState<number>(50);
  const [heroSettings, setHeroSettings] = useState<HeroImageSettings>({
    position: 'crop',
    zoom: 1.0,
    customX: 50,
    customY: 50,
    cropBox: {
      x: 25,
      y: 25,
      width: 50,
      height: 50
    }
  });

  const [showCloudinaryBrowser, setShowCloudinaryBrowser] = useState(false);
  const [imageAltTexts, setImageAltTexts] = useState<{[key: number]: string}>({});
  const [editorTab, setEditorTab] = useState<'details' | 'images' | 'cover'>('details');
  const [deletePromptOpen, setDeletePromptOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const openedGalleryIdRef = useRef<string | null>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const imageGridRef = useRef<HTMLDivElement>(null);
  const isDraggingImagesRef = useRef(false);
  const draggedIndexRef = useRef<number | null>(null);
  const pendingScrollToTopRef = useRef(false);
  const dragPointerYRef = useRef(0);
  const dragPointerAtRef = useRef(0);
  const dragScrollFrameRef = useRef<number | null>(null);
  const dropTargetRef = useRef<number | null>(null);
  const dragPreviewRef = useRef<HTMLDivElement>(null);

  // Initialize form with gallery data when gallery changes
  useEffect(() => {
    if (gallery) {
      setGalleryName(gallery.name);
      setGalleryCategories(selectedCategorySlugs(gallery));
      setGalleryDescription(gallery.description || '');
      setGalleryExcerpt(gallery.excerpt || '');
      setGalleryImages(gallery.images || []);
      setHeroImagePosition(gallery.heroImagePosition || 'crop');
      setHeroImageZoom(gallery.heroImageZoom || 1.0);
      setHeroImageCustomX(gallery.heroImageCustomX || 50);
      setHeroImageCustomY(gallery.heroImageCustomY || 50);
      setHeroSettings({
        position: gallery.heroImagePosition || 'crop',
        zoom: gallery.heroImageZoom || 1.0,
        customX: gallery.heroImageCustomX || 50,
        customY: gallery.heroImageCustomY || 50,
        cropBox: gallery.heroImageCropBox || {
          x: 25,
          y: 25,
          width: 50,
          height: 50
        }
      });
      
      // Initialize alt texts from existing images
      const altTexts: {[key: number]: string} = {};
      if (gallery.images) {
        gallery.images.forEach((image, index) => {
          altTexts[index] = image.alt || gallery.name;
        });
      }
      setImageAltTexts(altTexts);
      
      if (gallery.images && gallery.images.length > 0) {
        setShowImageManagement(true);
      }
    }
  }, [gallery]);

  // Load dynamic categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const serverCategories = await loadCategoriesFromServer();
        const options = getCategoryOptions(serverCategories);
        setCategoryOptions(options);
      } catch (error) {
        console.error('Error loading categories:', error);
        setCategoryOptions([]);
      }
    };
    
    loadCategories();
  }, []);

  useEffect(() => {
    if (isOpen && gallery) {
      setShowImageManagement(gallery.images && gallery.images.length > 0);
      setShowHeroSelection(gallery.images && gallery.images.length > 0);
    }
  }, [isOpen, gallery]);

  useEffect(() => {
    if (!isOpen || !gallery) {
      openedGalleryIdRef.current = null;
      return;
    }
    if (openedGalleryIdRef.current === gallery.id) return;
    openedGalleryIdRef.current = gallery.id;
    setEditorTab('details');
    setDeletePromptOpen(false);
    setDeleteConfirmText('');
  }, [isOpen, gallery]);

  const handleSave = () => {
    if (!gallery) {
      console.error('No gallery to save');
      return;
    }

    const chosenCategories = uniqueCategorySlugs(galleryCategories);
    if (!galleryName.trim() || chosenCategories.length === 0) {
      setEditorTab('details');
      window.setTimeout(() => {
        const fieldId = !galleryName.trim() ? 'edit-gallery-name' : 'edit-gallery-category';
        document.getElementById(fieldId)?.focus();
      }, 0);
      return;
    }

    // Update images with alt text
    const imagesWithAlt = galleryImages.map((image, index) => ({
      ...image,
      alt: imageAltTexts[index] || image.alt || galleryName
    }));

    // Clear cropBox if it's the default (25, 25, 50, 50) - means it was reset
    let cropBoxToSave = heroSettings.cropBox;
    if (cropBoxToSave && 
        cropBoxToSave.x === 25 && 
        cropBoxToSave.y === 25 && 
        cropBoxToSave.width === 50 && 
        cropBoxToSave.height === 50) {
      cropBoxToSave = undefined; // Clear it if it's the default
    }

    // Clear position/zoom/custom coords if position is 'default' - means it was reset
    let positionToSave: 'default' | 'center' | 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'face' | 'custom' | 'crop' | undefined = heroSettings.position;
    let zoomToSave: number | undefined = heroSettings.zoom;
    let customXToSave: number | undefined = heroSettings.customX;
    let customYToSave: number | undefined = heroSettings.customY;
    
    if (positionToSave === 'default') {
      positionToSave = undefined; // Clear position when reset
      zoomToSave = undefined; // Clear zoom when reset
      customXToSave = undefined; // Clear custom X when reset
      customYToSave = undefined; // Clear custom Y when reset
    }

    const updatedGallery: Gallery = {
      ...gallery,
      name: galleryName,
      category: chosenCategories[0],
      categories: chosenCategories,
      description: galleryDescription,
      excerpt: galleryExcerpt,
      images: imagesWithAlt,
      heroImage: imagesWithAlt[0], // Always use first image as hero - UPDATE when reordered!
      heroImageIndex: 0, // Always use first image as hero (simplest approach)
      heroImagePosition: positionToSave, // Add hero image positioning (or undefined if reset)
      heroImageZoom: zoomToSave, // Add hero image zoom level (or undefined if reset)
      heroImageCustomX: customXToSave, // Add custom X coordinate (or undefined if reset)
      heroImageCustomY: customYToSave, // Add custom Y coordinate (or undefined if reset)
      heroImageCropBox: cropBoxToSave, // Add crop box coordinates (or undefined if reset)
      updatedAt: new Date().toISOString()
    };

    console.log('🎯 Saving gallery from modal:', updatedGallery);
    console.log('🎯 Hero Image Position being saved:', heroImagePosition);
    console.log('🎯 Hero Image Zoom being saved:', heroImageZoom);
    console.log('🎯 Full gallery object:', JSON.stringify(updatedGallery, null, 2));
    onSave(updatedGallery);
    onClose();
  };

  const deleteNameMatches = Boolean(
    gallery && deleteConfirmText.trim() === gallery.name.trim() && gallery.name.trim()
  );

  const handleDelete = () => {
    if (!gallery || !onDelete || !deleteNameMatches) return;
    onDelete(gallery.id);
    onClose();
  };

  const handleImageUpload = (images: CloudinaryImage[]) => {
    if (images && images.length > 0) {
      const updatedImages = [...galleryImages, ...images];
      setGalleryImages(updatedImages);
      setShowImageManagement(true);
      setShowHeroSelection(true);
      updateHeroOptions(updatedImages);
      
      // Initialize alt text for new images
      const newAltTexts = {...imageAltTexts};
      images.forEach((_, index) => {
        const globalIndex = galleryImages.length + index;
        newAltTexts[globalIndex] = galleryName || `Image ${globalIndex + 1}`;
      });
      setImageAltTexts(newAltTexts);
    }
  };

  // Handle Cloudinary browser selection - direct access
  const handleCloudinaryImageSelect = () => {
    setShowCloudinaryBrowser(true);
  };

  const handleCloudinaryImagesSelected = (selectedImages: CloudinaryImage[]) => {
    const updatedImages = [...galleryImages, ...selectedImages];
    setGalleryImages(updatedImages);
    setShowImageManagement(true);
    setShowHeroSelection(true);
    updateHeroOptions(updatedImages);
    setShowCloudinaryBrowser(false);
    
    // Initialize alt text for new images
    const newAltTexts = {...imageAltTexts};
    selectedImages.forEach((_, index) => {
      const globalIndex = galleryImages.length + index;
      newAltTexts[globalIndex] = galleryName || `Image ${globalIndex + 1}`;
    });
    setImageAltTexts(newAltTexts);
  };

  const handleRemoveImage = (index: number) => {
    console.log('Removing image at index:', index);
    const newImages = galleryImages.filter((_, i) => i !== index);
    console.log('New images after removal:', newImages);
    setGalleryImages(newImages);
    
    // Update alt texts to match new indices
    const newAltTexts: {[key: number]: string} = {};
    newImages.forEach((_, i) => {
      if (i < index) {
        newAltTexts[i] = imageAltTexts[i] || '';
      } else {
        newAltTexts[i] = imageAltTexts[i + 1] || '';
      }
    });
    setImageAltTexts(newAltTexts);
    
    // Hero image is always the first image (index 0)
    updateHeroOptions(newImages);
  };

  // Hero image is always the first image, no need for this function
  // const handleSetHeroImage = (index: number) => {
  //   setHeroImageIndex(index);
  // };

  const handleSelectImage = (index: number) => {
    const newSelected = new Set(selectedImages);
    if (selectedImages.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedImages(newSelected);
  };

  const handleSelectAllImages = () => {
    const allSelected = new Set(galleryImages.map((_, i) => i));
    setSelectedImages(allSelected);
  };

  const handleDeselectAllImages = () => {
    setSelectedImages(new Set());
  };

  const handleDeleteSelectedImages = () => {
    if (selectedImages.size === 0) {
      console.log('No images selected for deletion');
      return;
    }
    
    console.log('Deleting selected images:', Array.from(selectedImages));
    const indexesToDelete = Array.from(selectedImages).sort((a, b) => b - a);
    let newImages = [...galleryImages];
    
    indexesToDelete.forEach(index => {
      newImages.splice(index, 1);
    });
    
    console.log('New images after bulk deletion:', newImages);
    setGalleryImages(newImages);
    setSelectedImages(new Set());
    
    // Hero image is always the first image (index 0)
    updateHeroOptions(newImages);
  };

  const handleClearAllImages = () => {
    if (window.confirm('Are you sure you want to remove all images?')) {
      setGalleryImages([]);
      setSelectedImages(new Set());
      setShowHeroSelection(false);
      updateHeroOptions([]);
    }
  };

  const handleImageReorder = (fromIndex: number, toIndex: number) => {
    const newImages = [...galleryImages];
    const [removed] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, removed);
    setGalleryImages(newImages);
    
    // Update alt texts to match new indices
    const newAltTexts: {[key: number]: string} = {};
    newImages.forEach((_, i) => {
      if (i < toIndex && i < fromIndex) {
        newAltTexts[i] = imageAltTexts[i] || '';
      } else if (i === toIndex) {
        newAltTexts[i] = imageAltTexts[fromIndex] || '';
      } else if (i > toIndex && i <= fromIndex) {
        newAltTexts[i] = imageAltTexts[i - 1] || '';
      } else {
        newAltTexts[i] = imageAltTexts[i] || '';
      }
    });
    setImageAltTexts(newAltTexts);
    
    // Hero image is always the first image (index 0)
    updateHeroOptions(newImages);
  };

  const placeDropTarget = (next: number) => {
    if (dropTargetRef.current === next) return;
    dropTargetRef.current = next;
    setDropTargetIndex(next);
  };

  const releaseCardDrag = (card: HTMLElement | null) => {
    if (card) card.draggable = false;
  };

  const armCardDrag = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('.image-selection-checkbox')) {
      releaseCardDrag(event.currentTarget);
      return;
    }
    event.currentTarget.draggable = true;
  };

  // Drag and drop handlers for image reordering
  const handleImageDragStart = (e: React.DragEvent, index: number) => {
    const groupDrag = selectedImages.size > 1 && selectedImages.has(index);
    isDraggingImagesRef.current = true;
    draggedIndexRef.current = index;
    dragPointerYRef.current = e.clientY;
    dragPointerAtRef.current = performance.now();
    setDraggedImageIndex(index);
    setIsGroupDragging(groupDrag);
    placeDropTarget(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try {
        e.dataTransfer.setData('text/plain', String(index));
      } catch (_) {
        // Some browsers block setData outside a user gesture.
      }
      if (groupDrag && dragPreviewRef.current) {
        try {
          e.dataTransfer.setDragImage(dragPreviewRef.current, 84, 52);
        } catch (_) {
          // A failed preview must not cancel the drag.
        }
      }
    }
    startImageDragScroll();
  };

  const handleImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  };

  const handleItemDragOver = (event: React.DragEvent, index: number) => {
    handleImageDragOver(event);
    rememberDragPointer(event);
    const fromIndex = draggedIndexRef.current;
    if (fromIndex === null) return;
    const groupDrag = selectedImages.size > 1 && fromIndex !== null && selectedImages.has(fromIndex);
    if (groupDrag && selectedImages.has(index)) return;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const after = event.clientX > rect.left + rect.width / 2;
    placeDropTarget(after ? index + 1 : index);
  };

  const rememberDragPointer = (event: React.DragEvent) => {
    if (!isDraggingImagesRef.current) return;
    if (event.clientY === 0 && event.clientX === 0) return;
    dragPointerYRef.current = event.clientY;
    dragPointerAtRef.current = performance.now();
  };

  const clearDragState = () => {
    isDraggingImagesRef.current = false;
    draggedIndexRef.current = null;
    dropTargetRef.current = null;
    stopImageDragScroll();
    setDraggedImageIndex(null);
    setDropTargetIndex(null);
    setIsGroupDragging(false);
  };

  const handleImageDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const fromIndex = draggedIndexRef.current;
    const targetIndex = dropTargetRef.current ?? dropIndex;
    // If multiple images are selected and we're dragging one of them, do bulk reorder
    if (fromIndex !== null && selectedImages.size > 1 && selectedImages.has(fromIndex)) {
      handleBulkReorder(targetIndex);
    } else if (fromIndex !== null && fromIndex !== targetIndex) {
      handleImageReorder(fromIndex, targetIndex);
    }

    clearDragState();
  };

  const handleImageDragEnd = () => {
    clearDragState();
  };

  // Get image orientation (portrait/landscape/square)
  const getImageOrientation = (image: CloudinaryImage): 'portrait' | 'landscape' | 'square' | null => {
    if (!image) return null;
    
    // Check if we have width and height properties
    if (image.width && image.height) {
      const ratio = image.width / image.height;
      if (ratio > 1.1) return 'landscape';
      if (ratio < 0.9) return 'portrait';
      return 'square';
    }
    
    // If no dimensions, return null
    return null;
  };

  const stopImageDragScroll = () => {
    if (dragScrollFrameRef.current !== null) {
      cancelAnimationFrame(dragScrollFrameRef.current);
      dragScrollFrameRef.current = null;
    }
  };

  const getPhotoScroller = () => {
    const pane = modalBodyRef.current;
    if (pane && pane.scrollHeight > pane.clientHeight + 8) return pane;
    const layout = pane?.parentElement;
    if (layout && layout.scrollHeight > layout.clientHeight + 8) return layout;
    return pane;
  };

  const scrollImageGridToTop = () => {
    const body = getPhotoScroller();
    const grid = imageGridRef.current;
    if (!body || !grid) return;
    const target = grid.querySelector('.image-item-wrapper') || grid;
    const nextTop = body.scrollTop + (target.getBoundingClientRect().top - body.getBoundingClientRect().top) - 8;
    body.scrollTop = Math.max(0, nextTop);
  };

  // The dialog body is its own scroll container, so a native drag does not
  // move it. Scroll while the pointer sits near the top or bottom edge.
  const runImageDragScroll = () => {
    if (!isDraggingImagesRef.current) {
      dragScrollFrameRef.current = null;
      return;
    }

    const body = getPhotoScroller();
    const pointerIsFresh = performance.now() - dragPointerAtRef.current < 300;

    if (body && isDraggingImagesRef.current && pointerIsFresh) {
      const edge = 140;
      const maxSpeed = 56;
      const rect = body.getBoundingClientRect();
      const y = dragPointerYRef.current;

      if (y < rect.top + edge) {
        const ratio = Math.min(1, (rect.top + edge - y) / edge);
        body.scrollTop -= Math.max(6, Math.round(ratio * maxSpeed));
      } else if (y > rect.bottom - edge) {
        const ratio = Math.min(1, (y - (rect.bottom - edge)) / edge);
        body.scrollTop += Math.max(6, Math.round(ratio * maxSpeed));
      }
    }

    dragScrollFrameRef.current = requestAnimationFrame(runImageDragScroll);
  };

  const startImageDragScroll = () => {
    if (dragScrollFrameRef.current === null) {
      dragScrollFrameRef.current = requestAnimationFrame(runImageDragScroll);
    }
  };

  const handleModalDragOver = (event: React.DragEvent) => {
    if (!isDraggingImagesRef.current) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    dragPointerYRef.current = event.clientY;
    dragPointerAtRef.current = performance.now();
  };

  // Handle bulk reordering of selected images
  const handleBulkReorder = (targetIndex: number) => {
    if (selectedImages.size === 0) return;

    const selectedIndices = Array.from(selectedImages).sort((a, b) => a - b);
    const selectedSet = new Set(selectedIndices);
    const selectedItems = selectedIndices.map((index) => ({
      image: galleryImages[index],
      alt: imageAltTexts[index] || '',
    }));
    const remaining = galleryImages
      .map((image, index) => ({
        image,
        alt: imageAltTexts[index] || '',
      }))
      .filter((_, index) => !selectedSet.has(index));

    const removedBeforeTarget = selectedIndices.filter((index) => index < targetIndex).length;
    const insertAt = Math.max(0, Math.min(remaining.length, targetIndex - removedBeforeTarget));
    const merged = [
      ...remaining.slice(0, insertAt),
      ...selectedItems,
      ...remaining.slice(insertAt),
    ];
    const newImages = merged.map((item) => item.image);
    const newAltTexts: {[key: number]: string} = {};
    merged.forEach((item, index) => {
      newAltTexts[index] = item.alt;
    });

    setGalleryImages(newImages);
    setImageAltTexts(newAltTexts);
    setSelectedImages(new Set(selectedItems.map((_, offset) => insertAt + offset)));
    updateHeroOptions(newImages);

    if (insertAt === 0) {
      pendingScrollToTopRef.current = true;
    }
  };

  const updateHeroOptions = (images: CloudinaryImage[]) => {
    if (images.length === 0) {
      setShowHeroSelection(false);
    } else {
      setShowHeroSelection(true);
    }
  };

  // Drag events stop at the dialog edge. Keep tracking the pointer over the
  // page overlay so holding a selection at the top of the screen still scrolls.
  useEffect(() => {
    if (!isOpen) return;

    const trackPointer = (event: DragEvent) => {
      if (!isDraggingImagesRef.current) return;
      if (event.clientY === 0 && event.clientX === 0) return;
      if (event.type === 'dragover') event.preventDefault();
      dragPointerYRef.current = event.clientY;
      dragPointerAtRef.current = performance.now();
    };

    document.addEventListener('dragover', trackPointer);
    document.addEventListener('drag', trackPointer);
    return () => {
      document.removeEventListener('dragover', trackPointer);
      document.removeEventListener('drag', trackPointer);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!pendingScrollToTopRef.current) return;
    pendingScrollToTopRef.current = false;
    scrollImageGridToTop();
    const frame = requestAnimationFrame(scrollImageGridToTop);
    const timer = window.setTimeout(scrollImageGridToTop, 60);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [galleryImages]);

  // Add modal-open class to body when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      isDraggingImagesRef.current = false;
      if (dragScrollFrameRef.current !== null) {
        cancelAnimationFrame(dragScrollFrameRef.current);
        dragScrollFrameRef.current = null;
      }
    };
  }, [isOpen]);

  const groupDragActive = isGroupDragging
    && draggedImageIndex !== null
    && selectedImages.size > 1
    && selectedImages.has(draggedImageIndex);
  let dropSlotIndex: number | null = null;
  if (dropTargetIndex !== null && draggedImageIndex !== null) {
    if (groupDragActive) {
      let nextVisible = dropTargetIndex;
      while (nextVisible < galleryImages.length && selectedImages.has(nextVisible)) {
        nextVisible += 1;
      }
      dropSlotIndex = nextVisible;
    } else if (dropTargetIndex !== draggedImageIndex) {
      dropSlotIndex = dropTargetIndex;
    }
  }
  const dragPreviewIndexes = (selectedImages.size > 1
    ? Array.from(selectedImages)
    : draggedImageIndex !== null ? [draggedImageIndex] : []
  ).sort((a, b) => a - b).slice(0, 3);
  const dragPreviewCount = selectedImages.size > 1 ? selectedImages.size : 1;

  const renderDropSlot = (key: string) => (
    <div
      key={key}
      className="image-drop-slot"
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        rememberDragPointer(event);
      }}
      onDrop={(event) => handleImageDrop(event, dropSlotIndex ?? 0)}
    >
      <div className="drag-stack">
        {dragPreviewIndexes.map((imageIndex, stackIndex) => {
          const image = galleryImages[imageIndex];
          if (!image) return null;
          return (
            <img
              key={image.publicId || image.url || stackIndex}
              src={image.url || image.secure_url}
              alt=""
              draggable={false}
            />
          );
        })}
      </div>
      <strong>Drop {dragPreviewCount} photo{dragPreviewCount === 1 ? '' : 's'} here</strong>
      <span>This is where they will land</span>
    </div>
  );

  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay">
      <div className="large-edit-modal" onDragOver={handleModalDragOver}>
        <div className="modal-header">
          <h3>Edit Gallery: {gallery?.name}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        
        <div className="modal-body">
          <form
            className="edit-gallery-form"
            noValidate
            onSubmit={(e) => { 
            e.preventDefault(); 
            console.log('Form submitted, calling handleSave');
            handleSave(); 
          }}>
            <div className="editor-tabs" role="tablist" aria-label="Gallery editor">
              <button
                type="button"
                role="tab"
                id="gallery-tab-details-btn"
                aria-controls="gallery-tab-details"
                aria-selected={editorTab === 'details'}
                className={`editor-tab ${editorTab === 'details' ? 'active' : ''}`}
                onClick={() => setEditorTab('details')}
              >
                Gallery details
              </button>
              <button
                type="button"
                role="tab"
                id="gallery-tab-images-btn"
                aria-controls="gallery-tab-images"
                aria-selected={editorTab === 'images'}
                className={`editor-tab ${editorTab === 'images' ? 'active' : ''}`}
                onClick={() => setEditorTab('images')}
              >
                Images
              </button>
              <button
                type="button"
                role="tab"
                id="gallery-tab-cover-btn"
                aria-controls="gallery-tab-cover"
                aria-selected={editorTab === 'cover'}
                className={`editor-tab ${editorTab === 'cover' ? 'active' : ''}`}
                onClick={() => setEditorTab('cover')}
              >
                Cover & crop
              </button>
            </div>

            <div className="editor-tab-panels">
            <div
              role="tabpanel"
              id="gallery-tab-details"
              aria-labelledby="gallery-tab-details-btn"
              className="editor-tab-panel editor-tab-panel-details"
              hidden={editorTab !== 'details'}
            >
            <div className="form-group">
              <label htmlFor="edit-gallery-name">Gallery Name</label>
              <input
                type="text"
                id="edit-gallery-name"
                value={galleryName}
                onChange={(e) => setGalleryName(e.target.value)}
                required
              />
            </div>

            <fieldset
              id="edit-gallery-category"
              tabIndex={-1}
              className="form-group gallery-category-choices"
              aria-describedby="edit-gallery-category-hint"
            >
              <legend>Categories</legend>
              {categoryOptions.map((option) => (
                <label key={option.value}>
                  <input
                    type="checkbox"
                    checked={categoryIsChosen(galleryCategories, option.value)}
                    onChange={() => setGalleryCategories((current) => toggleCategorySlug(current, option.value))}
                  />
                  {option.label}
                </label>
              ))}
              <span id="edit-gallery-category-hint" className="field-hint">
                Tick every category this gallery should appear in. The first one you tick is the main category.
              </span>
            </fieldset>

            <div className="form-group">
              <label htmlFor="edit-gallery-excerpt">
                Short Excerpt
                <span className="field-hint">1-2 sentences for gallery cards</span>
              </label>
              <textarea
                id="edit-gallery-excerpt"
                value={galleryExcerpt}
                onChange={(e) => setGalleryExcerpt(e.target.value)}
                rows={2}
                maxLength={200}
                placeholder="Brief preview text that appears on gallery cards..."
              />
              <div className="field-count">
                {galleryExcerpt.length}/200 characters
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="edit-gallery-description">
                Full Description
                <span className="field-hint">Detailed text, appears in the gallery view</span>
              </label>
              <textarea
                id="edit-gallery-description"
                value={galleryDescription}
                onChange={(e) => setGalleryDescription(e.target.value)}
                rows={5}
                placeholder="Full detailed description of this gallery..."
              />
            </div>

            {onDelete && gallery && (
              <section className="gallery-danger-zone" aria-labelledby="gallery-danger-heading">
                <h4 id="gallery-danger-heading">Danger zone</h4>
                <p>Delete “{gallery.name}” permanently. Other galleries stay as they are.</p>
                {!deletePromptOpen ? (
                  <button
                    type="button"
                    className="btn btn-danger-quiet"
                    onClick={() => {
                      setDeleteConfirmText('');
                      setDeletePromptOpen(true);
                    }}
                  >
                    Delete Gallery
                  </button>
                ) : (
                  <div className="gallery-danger-confirm">
                    <label htmlFor="delete-gallery-confirm">
                      Type <strong>{gallery.name}</strong> to confirm deletion.
                    </label>
                    <input
                      id="delete-gallery-confirm"
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      autoComplete="off"
                      placeholder="Gallery name"
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter') return;
                        event.preventDefault();
                        if (deleteNameMatches) handleDelete();
                      }}
                    />
                    <div className="gallery-danger-confirm-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setDeletePromptOpen(false);
                          setDeleteConfirmText('');
                        }}
                      >
                        Keep Gallery
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger-quiet"
                        disabled={!deleteNameMatches}
                        onClick={handleDelete}
                      >
                        Delete this gallery
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}
            </div>

            <div
              role="tabpanel"
              id="gallery-tab-images"
              aria-labelledby="gallery-tab-images-btn"
              className="editor-tab-panel editor-tab-panel-images"
              hidden={editorTab !== 'images'}
              ref={modalBodyRef}
            >
            <div className="upload-actions">
              <UploadWidget
                onUpload={(images: CloudinaryImage[]) => {
                  handleImageUpload(images);
                }}
                buttonText="Add Images to Cloudinary"
                folder={galleryName ? `carmen_lorraine_photos/galleries/${galleryName.toLowerCase().replace(/\s+/g, '_')}` : 'carmen_lorraine_photos/galleries/temp'}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloudinaryImageSelect}
                title="Browse current website gallery folders (carmen_lorraine_photos/galleries)"
              >
                <i className="fas fa-folder-open"></i> Browse Cloudinary
              </button>
            </div>

            {/* Image Management Section */}
            {showImageManagement && (
              <div className="image-management-section">
                <div className="image-section-header">
                  <h4>Gallery Images <span className="image-count">({galleryImages.length} images)</span></h4>
                  <p className="image-reorder-hint">Drag any photo to reorder. The first photo is the cover.</p>
                </div>

                {galleryImages.length > 0 && (
                  <div className="bulk-image-actions">
                    <div className="bulk-actions-info">
                      <span className="selection-count">
                        {selectedImages.size} of {galleryImages.length} selected
                      </span>
                    </div>
                    <div className="bulk-actions-buttons">
                      <div className="bulk-actions-safe">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={handleSelectAllImages}
                        >
                          <i className="fas fa-check-square"></i> Select All
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={handleDeselectAllImages}
                        >
                          <i className="fas fa-square"></i> Deselect All
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={selectedImages.size === 0}
                          onClick={() => handleBulkReorder(0)}
                          title="Move the selected images to the start of the gallery"
                        >
                          <i className="fas fa-arrow-up"></i> Move Selected to Top
                        </button>
                      </div>
                      <div className="bulk-actions-danger">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          disabled={selectedImages.size === 0}
                          onClick={handleDeleteSelectedImages}
                        >
                          <i className="fas fa-trash"></i> Delete Selected ({selectedImages.size})
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={handleClearAllImages}
                        >
                          <i className="fas fa-trash"></i> Delete All Images
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {selectedImages.size > 1 && dragPreviewIndexes.length > 0 && (
                  <div className="drag-preview-card" ref={dragPreviewRef} aria-hidden="true">
                    <div className="drag-stack">
                      {dragPreviewIndexes.map((imageIndex, stackIndex) => {
                        const image = galleryImages[imageIndex];
                        if (!image) return null;
                        return (
                          <img
                            key={image.publicId || image.url || stackIndex}
                            src={image.url || image.secure_url}
                            alt=""
                            draggable={false}
                          />
                        );
                      })}
                    </div>
                    <span className="drag-stack-label">
                      {dragPreviewCount} photo{dragPreviewCount === 1 ? '' : 's'}
                    </span>
                  </div>
                )}

                <div
                  className={`image-grid drag-mode ${selectedImages.size > 0 ? 'has-selection' : ''} ${groupDragActive ? 'is-group-dragging' : ''}`}
                  ref={imageGridRef}
                >
                  {galleryImages.map((image, index) => (
                    <React.Fragment key={image.publicId || image.url || index}>
                    {dropSlotIndex === index && renderDropSlot(`drop-${index}`)}
                    <div 
                      className={`image-item-wrapper ${draggedImageIndex === index ? 'dragging' : ''} ${groupDragActive && selectedImages.has(index) && draggedImageIndex !== index ? 'is-in-drag-group' : ''}`}
                      onDragOver={(e) => handleItemDragOver(e, index)}
                      onDrop={(e) => handleImageDrop(e, index)}
                    >
                      <div
                        className={`image-item ${index === 0 ? 'is-hero' : ''} ${selectedImages.has(index) ? 'is-selected' : ''} is-draggable-card`}
                        draggable={false}
                        onMouseDown={armCardDrag}
                        onDragStart={(e) => handleImageDragStart(e, index)}
                        onDrag={rememberDragPointer}
                        onDragEnd={(event) => {
                          handleImageDragEnd();
                          releaseCardDrag(event.currentTarget);
                        }}
                      >
                        <div className="drag-handle" title="Drag to reorder">
                          <i className="fas fa-grip-vertical"></i>
                        </div>
                        <img 
                          src={image.url || image.secure_url} 
                          alt={imageAltTexts[index] || `Gallery image ${index + 1}`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.jpg';
                          }}
                          draggable={false}
                        />
                        
                        {/* Selection Checkbox */}
                        <div
                          className="image-selection-checkbox"
                          onMouseDown={(event) => {
                            event.stopPropagation();
                            releaseCardDrag(event.currentTarget.closest('.image-item'));
                          }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            draggable={false}
                            checked={selectedImages.has(index)}
                            onChange={() => handleSelectImage(index)}
                            title="Select for bulk actions"
                          />
                        </div>
                        
                        {/* Orientation Badge */}
                        {(() => {
                          const orientation = getImageOrientation(image);
                          if (orientation) {
                            return (
                              <span 
                                className={`image-orientation-badge orientation-${orientation}`} 
                                title={`${orientation.charAt(0).toUpperCase() + orientation.slice(1)} orientation`}
                              >
                                <i className={`fas fa-${orientation === 'portrait' ? 'mobile-alt' : orientation === 'landscape' ? 'desktop' : 'square'}`}></i>
                              </span>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      
                      {/* Alt Text Input - Now Below Image */}
                      <div className="image-alt-pill">
                        <i className="fas fa-tag"></i>
                        <input
                          type="text"
                          value={imageAltTexts[index] || ''}
                          onChange={(e) => setImageAltTexts({...imageAltTexts, [index]: e.target.value})}
                          placeholder="Alt text for SEO..."
                          className="alt-text-input"
                          title={imageAltTexts[index] || 'Alt text for SEO'}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      
                    </div>
                    </React.Fragment>
                  ))}
                  {dropSlotIndex === galleryImages.length && renderDropSlot('drop-end')}
                </div>
              </div>
            )}
            </div>

            <div
              role="tabpanel"
              id="gallery-tab-cover"
              aria-labelledby="gallery-tab-cover-btn"
              className="editor-tab-panel editor-tab-panel-cover"
              hidden={editorTab !== 'cover'}
            >
              {galleryImages.length > 0 ? (
                <HeroImageEditor
                  heroImage={galleryImages[0] || null}
                  currentSettings={heroSettings}
                  onSettingsChange={(newSettings) => {
                    console.log('🎯 GalleryEditModal: Hero settings changed', newSettings);
                    setHeroSettings(newSettings);
                    setHeroImagePosition(newSettings.position);
                    setHeroImageZoom(newSettings.zoom);
                    setHeroImageCustomX(newSettings.customX || 50);
                    setHeroImageCustomY(newSettings.customY || 50);
                  }}
                  targetWidth={800}
                  targetHeight={600}
                />
              ) : (
                <p className="editor-tab-empty">Add images first, then choose a cover and crop. The first image is the cover.</p>
              )}
            </div>
            </div>

            <div className="modal-actions">
              <div className="modal-actions-main">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary modal-save"
                >
                  <i className="fas fa-save"></i> Save Changes
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>


      {/* Cloudinary Browser - Direct Access */}
      {showCloudinaryBrowser && (
        <CloudinaryImageBrowser
          onImagesSelected={handleCloudinaryImagesSelected}
          onClose={() => setShowCloudinaryBrowser(false)}
          existingImages={galleryImages.map(img => img.publicId)}
          maxSelection={50}
          insideModal={false}
          showContinueButton={true}
        />
      )}

    </div>,
    document.body
  );
};

export default GalleryEditModal;
